import React, { useState, useEffect } from 'react';
import { accountingAPI, formatCurrency } from '../services/api';
import { toast } from 'sonner';
import { Users, TrendingUp, AlertCircle, DollarSign } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#f97316', '#10b981', '#6366f1', '#ef4444', '#f59e0b', '#8b5cf6', '#14b8a6', '#ec4899'];

export default function ClientAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    accountingAPI.clientAnalytics().then(r => setData(r.data))
      .catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>;

  const revenueChart = data?.clients?.slice(0, 8).map(c => ({ name: c.client_name.length > 15 ? c.client_name.slice(0, 15) + '...' : c.client_name, revenue: c.total_revenue, outstanding: c.outstanding })) || [];
  const pieData = data?.clients?.slice(0, 6).map(c => ({ name: c.client_name.length > 15 ? c.client_name.slice(0, 15) + '...' : c.client_name, value: c.total_revenue })) || [];

  return (
    <div data-testid="client-analytics-page" className="space-y-5">
      <div>
        <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900">Client Analytics</h1>
        <p className="text-sm text-slate-500 mt-1">Revenue, outstanding balances & profit contribution per client</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="ca-total-clients">
          <div className="flex items-start justify-between">
            <div><p className="text-[10px] font-semibold text-slate-500 uppercase">Total Clients</p><p className="text-2xl font-bold mt-1">{data?.total_clients || 0}</p></div>
            <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center"><Users className="w-5 h-5 text-slate-600" /></div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="ca-total-revenue">
          <div className="flex items-start justify-between">
            <div><p className="text-[10px] font-semibold text-emerald-600 uppercase">Total Revenue</p><p className="text-2xl font-bold mt-1 text-emerald-700 font-mono">Rs. {formatCurrency(data?.total_revenue || 0)}</p></div>
            <div className="w-10 h-10 rounded-md bg-emerald-50 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-emerald-600" /></div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="ca-total-outstanding">
          <div className="flex items-start justify-between">
            <div><p className="text-[10px] font-semibold text-amber-600 uppercase">Total Outstanding</p><p className="text-2xl font-bold mt-1 text-amber-700 font-mono">Rs. {formatCurrency(data?.total_outstanding || 0)}</p></div>
            <div className="w-10 h-10 rounded-md bg-amber-50 flex items-center justify-center"><AlertCircle className="w-5 h-5 text-amber-600" /></div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="ca-collection-rate">
          <div className="flex items-start justify-between">
            <div><p className="text-[10px] font-semibold text-blue-600 uppercase">Collection Rate</p><p className="text-2xl font-bold mt-1 text-blue-700">{data?.total_revenue > 0 ? Math.round(((data.total_revenue - data.total_outstanding) / data.total_revenue) * 100) : 0}%</p></div>
            <div className="w-10 h-10 rounded-md bg-blue-50 flex items-center justify-center"><DollarSign className="w-5 h-5 text-blue-600" /></div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {revenueChart.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <h3 className="font-heading font-semibold text-slate-800 mb-4 text-sm">Client Revenue vs Outstanding</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={revenueChart} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={120} />
                <Tooltip formatter={(v) => `Rs. ${formatCurrency(v)}`} />
                <Bar dataKey="revenue" fill="#10b981" radius={[0, 4, 4, 0]} name="Revenue" />
                <Bar dataKey="outstanding" fill="#f59e0b" radius={[0, 4, 4, 0]} name="Outstanding" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        {pieData.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <h3 className="font-heading font-semibold text-slate-800 mb-4 text-sm">Revenue Distribution</h3>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => `Rs. ${formatCurrency(v)}`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full data-table" data-testid="ca-table">
          <thead>
            <tr><th>Client Name</th><th className="text-right">Revenue</th><th className="text-right">GST</th><th className="text-center">Invoices</th><th className="text-center">Paid</th><th className="text-right">Outstanding</th><th className="text-right">Contribution %</th></tr>
          </thead>
          <tbody>
            {data?.clients?.map((c, i) => (
              <tr key={i} data-testid={`ca-row-${i}`}>
                <td className="text-sm font-medium text-slate-800">{c.client_name}</td>
                <td className="text-right font-mono text-sm text-emerald-700">Rs. {formatCurrency(c.total_revenue)}</td>
                <td className="text-right font-mono text-xs text-slate-500">Rs. {formatCurrency(c.total_gst)}</td>
                <td className="text-center text-sm">{c.invoice_count}</td>
                <td className="text-center text-sm text-emerald-600">{c.paid_count}</td>
                <td className="text-right font-mono text-sm">
                  <span className={c.outstanding > 0 ? 'text-amber-600 font-semibold' : 'text-slate-400'}>Rs. {formatCurrency(c.outstanding)}</span>
                </td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-16 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-orange-500 rounded-full" style={{ width: `${Math.min(c.profit_contribution, 100)}%` }} />
                    </div>
                    <span className="text-xs font-mono text-slate-600">{c.profit_contribution}%</span>
                  </div>
                </td>
              </tr>
            ))}
            {(!data?.clients || data.clients.length === 0) && (
              <tr><td colSpan={7} className="text-center text-slate-400 py-12">No client data. Create invoices to see analytics.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
